/**
 * ErrorBoundary - Componente para captura de erros React
 *
 * Captura erros JavaScript não tratados durante renderização,
 * lifecycle methods e construtores.
 *
 * NÃO captura: event handlers, erros assíncronos, SSR, ou erros no próprio ErrorBoundary.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import Logger from '@/utils/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, errorInfo: ErrorInfo) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  boundaryName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const componentStack = errorInfo.componentStack || 'Component stack not available';

    Logger.error('React Error Boundary caught an error', {
      error,
      componentStack,
      boundaryName: this.props.boundaryName || 'default',
      errorBoundary: true,
    });

    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (callbackError) {
        Logger.error('Error in ErrorBoundary onError callback', {
          error: callbackError,
        });
      }
    }

    this.setState({ errorInfo });
  }

  private handleReset = (shouldReload = false): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (shouldReload) {
      window.location.reload();
    }
  };

  private renderFallback(): ReactNode {
    const { fallback } = this.props;
    const { error, errorInfo } = this.state;

    if (fallback) {
      if (typeof fallback === 'function' && error) {
        return fallback(error, errorInfo ?? { componentStack: '' });
      }
      if (typeof fallback !== 'function') {
        return fallback;
      }
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: '600px',
            width: '100%',
            padding: '2rem',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#dc2626',
              marginBottom: '1rem',
            }}
          >
            Ops! Algo deu errado
          </h1>

          <p
            style={{
              fontSize: '1rem',
              color: '#6b7280',
              marginBottom: '1.5rem',
              lineHeight: '1.5',
            }}
          >
            Encontramos um problema inesperado. Nossa equipe foi notificada automaticamente e estamos trabalhando para
            resolver o problema.
          </p>

          {import.meta.env.DEV && error && (
            <details
              style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#fef2f2',
                borderRadius: '4px',
                fontSize: '0.875rem',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: '#dc2626',
                  marginBottom: '0.5rem',
                }}
              >
                Detalhes do erro (apenas em desenvolvimento)
              </summary>
              <pre
                style={{
                  marginTop: '0.5rem',
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  color: '#991b1b',
                }}
              >
                {error.toString()}
                {errorInfo?.componentStack}
              </pre>
            </details>
          )}

          <button
            type="button"
            onClick={() => this.handleReset(true)}
            style={{
              width: '100%',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '500',
              color: 'white',
              backgroundColor: '#2563eb',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1d4ed8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
